import { apiClient } from './client'
import { authService } from '@/api/auth'

// --- Binding taxonomy (mirrors lambda/goals-handler + the spec) ---
export type BindingKind =
  | 'funnel_step' | 'datalake_event' | 'custom_event' | 'propensity' | 'cohort' | 'store_funnel'
  | 'product' | 'search' | 'visitor_segment' | 'traffic_channel'

export interface Binding {
  kind: BindingKind
  metric: string
  // ref fields, by kind:
  funnel_id?: string
  step_id?: string
  event_name?: string
  event_id?: string
  cohort_id?: string
  product_id?: string
  channel?: string
  ref_status?: 'existing' | 'pending_new'
  new_primitive_key?: string
}

export const BINDING_METRICS: Record<BindingKind, string[]> = {
  funnel_step: ['step_sessions', 'step_unique_visitors', 'step_conversion_rate', 'drop_off_rate'],
  datalake_event: ['occurrences'],
  custom_event: ['total_occurrences', 'unique_visitors', 'unique_sessions', 'visitor_engagement_rate', 'session_engagement_rate'],
  propensity: ['conversion_lift', 'conversion_lift_percentage'],
  cohort: ['sessions', 'unique_visitors', 'conversions', 'conversion_rate', 'revenue'],
  store_funnel: ['conversion_rate', 'total_funnel_hits', 'revenue', 'aov'],
  // Per-SKU mechanism metrics (FW-1 D2). Absent product in a present index reads 0 — the
  // "views > 0" verification for a fixed broken-image SKU. conversion_rate = the BB-able
  // in-context read (conversions/views) for product actionables.
  product: ['views', 'conversions', 'revenue', 'conversion_rate'],
  // On-site search (INS-1). usage_rate = the buscador-CTA mechanism metric.
  search: ['usage_rate', 'searcher_conversion_rate', 'search_sessions'],
  // ga4-engagement-v1 segments — the same segmentation Resultados renders (bounce = bouncers_share).
  visitor_segment: ['bouncers_share', 'browsers_share', 'shoppers_share', 'buyers_share'],
  // GA4-named traffic channels (FW-7). `share` = the email-channel goal's mechanism.
  traffic_channel: ['share', 'sessions', 'conversions', 'conversion_rate'],
}

export interface IndicatorStatus {
  state: 'unbound' | 'bound' | 'calibrating' | 'live' | 'stale' | 'informational'
  light: '🟢' | '🟡' | '🔴' | '⏳'
  value: number | null
  // rate metrics (Beta-Binomial + ROPE)
  p_worse?: number | null
  p_better?: number | null
  merchant?: 'probably_worse' | 'holding' | 'probably_better'
  // count/revenue metrics (forward-rate pace): headline = cumulative vs target;
  // light = recent_weekly vs required_weekly (the "will you make it" signal).
  pace?: number | null
  expected?: number | null
  actual?: number | null
  cumulative?: number | null
  target?: number | null
  pct?: number | null
  recent_weekly?: number | null
  required_weekly?: number | null
  achieved?: boolean
  breached?: boolean
  // P0.2 mix-shift guard ROUTING (only on CR-polarity reds): the guard routes a drop to
  // store-red (it's the site), traffic (it's acquisition), or insufficient (consultant decides).
  mix?: {
    disposition: 'store-red' | 'traffic' | 'insufficient'
    dimension?: 'origin' | 'device' | null
    residual?: number | null
    removed?: number | null
    coverage?: number | null
    merchantNextStep?: string
    annotate?: string
    segmented?: boolean // CR1 v1: a segmented red is consultant-routed (insufficient), never merchant
  }
}

const RATE_METRICS = new Set(['conversion_rate', 'step_conversion_rate', 'drop_off_rate', 'visitor_engagement_rate', 'session_engagement_rate', 'usage_rate', 'searcher_conversion_rate', 'bouncers_share', 'browsers_share', 'shoppers_share', 'buyers_share', 'share'])

/** Format a metric value for display (rate metrics are 0–1 fractions → %). */
export function formatMetricValue(metric: string | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  if (metric === 'aov' || metric === 'revenue') return `$${Math.round(value).toLocaleString()}`
  if (metric && RATE_METRICS.has(metric)) return `${(value * 100).toFixed(1)}%`
  if (metric === 'conversion_lift_percentage') return `${value.toFixed(1)}%`
  return value.toLocaleString()
}

// P0.2 ROUTER: a CR-polarity red is ROUTED, never muted to green. `traffic` → a DISTINCT merchant
// state with an *acquisition* next-step ("it's not the store, look at your traffic"). `store-red` and
// `insufficient` keep the red (insufficient also flags the consultant). Routing re-routes the signal;
// it never deletes it — a real drop is always sent somewhere, never turned on-track.
export function merchantStatus(goal: Goal): { status: GoalStatus | 'traffic'; light: Goal['status_light']; note?: string } {
  const c = goal.primary_metric?.current
  if (goal.status === 'at_risk' && c?.mix?.disposition === 'traffic') {
    return { status: 'traffic', light: '🟡', note: c.mix.merchantNextStep || c.mix.annotate || 'Revisá tu tráfico — no es la tienda.' }
  }
  return { status: goal.status, light: goal.status_light }
}

/** Merchant-facing trend chip from a resolved status: rate 3-state, or count pace. */
export function merchantTrend(c?: IndicatorStatus): { label: string; tone: 'up' | 'down' | 'flat' } | null {
  if (!c) return null
  if (c.merchant === 'probably_worse') return { label: 'Bajando', tone: 'down' }
  if (c.merchant === 'probably_better') return { label: 'Mejorando', tone: 'up' }
  if (c.merchant === 'holding') return { label: 'Estable', tone: 'flat' }
  if (c.pace != null) return c.pace >= 1 ? { label: 'En ritmo', tone: 'up' } : { label: 'Atrasado', tone: 'down' }
  return null
}

export interface Indicator {
  indicator_id?: string
  label: string
  binding: Binding
  direction: 'up_is_good' | 'down_is_good'
  state?: string
  baseline: { value: number; source: 'ga4_backfill' | 'calibration'; captured_at: string } | null
  threshold: { watch_pct?: number; risk_pct?: number; stall_days?: number }
  source?: 'consultant' | 'ai_assisted'
  // Segmented goal (CR1 v1): resolve the metric within ONE dimension cell (e.g. device=mobile).
  // Consultant instrument — its red is routed to the consultant, not the merchant, until v2.
  // For actionable mechanism indicators this doubles as the declared alcance (exposed population).
  segment?: { dimension: 'origin' | 'device'; key: string }
  // Bindability taxonomy: causal-confidence level of the binding↔change link, declared at
  // intake. N0/N1 (and undeclared) never earn a per-actionable veredicto — Desk gates it.
  nivel_binding?: 'N0' | 'N1' | 'N2' | 'N3' | 'N4'
  current?: IndicatorStatus // populated by the read path
}

export type GoalStatus = 'calibrating' | 'on_track' | 'watch' | 'at_risk' | 'achieved'

export interface Goal {
  goal_id: string
  store_id: string
  iteration_id: string
  iteration_start: string
  iteration_end: string | null
  name: string
  description: string
  primary_metric: Indicator
  target: { value: number; type: 'absolute' | 'relative_pct' } | null
  status: GoalStatus
  status_light: '🟢' | '🟡' | '🔴' | '⏳'
  impact?: { revenue_at_stake: number; basis: 'computed' | 'assigned' | 'none' }
  impact_tier?: 'high' | 'medium' | 'low'
  source: 'consultant' | 'ai_assisted'
  evidence?: Evidence | null
  created_at: string
  updated_at: string
  actionables?: Actionable[]
  actionables_count?: number
}

/** The motivating data snapshot ("Detalle") behind a goal/actionable — FW-2. */
export interface Evidence {
  summary: string
  metrics?: Array<{ label: string; value: string; source: 'tnx' | 'ga4' | 'tn_admin' | 'manual' | 'audit'; as_of?: string }>
  captured_at: string
  source: 'consultant' | 'ai_assisted' | 'audit'
}

export interface Actionable {
  actionable_id: string
  store_id: string
  goal_id: string
  name: string
  intent: string
  /** 'change' = a manual site/catalog intervention (FW-1 D3); with indicators it's instrumented. */
  type: 'observe' | 'instrument' | 'nudge' | 'experiment' | 'change'
  status: 'proposed' | 'active' | 'paused' | 'done' | 'dismissed'
  /** Stamped on the first status→active transition — when the change SHIPPED (FW-1 D1). Mechanism
   *  indicators measure before/after around this date, not created_at. */
  activated_at?: string | null
  indicators: Indicator[]
  evidence?: Evidence | null
  provisions: any[]
  experiment_id: string | null
  source: 'consultant' | 'ai_assisted'
  created_at: string
  updated_at: string
}

export interface Nudge {
  nudge_id: string
  store_id: string
  actionable_id: string
  goal_id: string
  created_at: string
  author: 'consultant' | 'ai_draft'
  status: 'draft' | 'sent' | 'dismissed'
  text: string
  trigger: any | null
  outcome: { indicator_value_after: number; moved: boolean; evaluated_at: string } | null
}

export interface GoalsListResponse {
  goals: Goal[]
  count: number
  window: { window_start: string; window_end: string; days: number; snapshots_used: number }
}

class GoalService {
  async listGoals(windowDays = 7): Promise<GoalsListResponse> {
    const storeId = authService.getCurrentStoreId()
    return apiClient.get<GoalsListResponse>('/api/goals', { store_id: storeId, window: windowDays })
  }

  /** Merchant execution brief (consultant-triggered): standalone HTML the consultant
   * reviews and shares by the usual channel. */
  async getBrief(): Promise<{ store_id: string; generated_at: string; html: string }> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.get<{ brief: { store_id: string; generated_at: string; html: string } }>(
      '/api/goals/brief', { store_id: storeId })
    return res.brief
  }

  async getGoal(goalId: string, windowDays = 7): Promise<Goal> {
    const res = await apiClient.get<{ goal: Goal }>(`/api/goals/${goalId}`, { window: windowDays })
    return res.goal
  }

  async createGoal(data: {
    name: string
    description?: string
    primary_metric: Indicator
    target?: Goal['target']
    impact_tier?: 'high' | 'medium' | 'low'
    iteration_end?: string
  }): Promise<Goal> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.post<{ goal: Goal }>('/api/goals', { ...data, store_id: storeId })
    return res.goal
  }

  async updateGoal(goalId: string, data: Partial<Goal>): Promise<Goal> {
    const res = await apiClient.put<{ goal: Goal }>(`/api/goals/${goalId}`, data)
    return res.goal
  }

  async archiveGoal(goalId: string): Promise<void> {
    await apiClient.delete(`/api/goals/${goalId}`)
  }

  async listActionables(goalId: string): Promise<Actionable[]> {
    const res = await apiClient.get<{ actionables: Actionable[] }>(`/api/goals/${goalId}/actionables`)
    return res.actionables || []
  }

  async createActionable(goalId: string, data: {
    name: string
    intent?: string
    type: Actionable['type']
    indicators?: Indicator[]
    evidence?: Partial<Evidence>
    activated_at?: string
    provisions?: any[]
  }): Promise<Actionable> {
    const res = await apiClient.post<{ actionable: Actionable }>(`/api/goals/${goalId}/actionables`, data)
    return res.actionable
  }

  async updateActionable(actionableId: string, data: Partial<Actionable>): Promise<Actionable> {
    const res = await apiClient.put<{ actionable: Actionable }>(`/api/actionables/${actionableId}`, data)
    return res.actionable
  }

  async dismissActionable(actionableId: string): Promise<void> {
    await apiClient.delete(`/api/actionables/${actionableId}`)
  }

  async listNudges(actionableId: string): Promise<Nudge[]> {
    const res = await apiClient.get<{ nudges: Nudge[] }>(`/api/actionables/${actionableId}/nudges`)
    return res.nudges || []
  }

  async addNudge(actionableId: string, data: { text: string; author?: string; trigger?: any }): Promise<Nudge> {
    const res = await apiClient.post<{ nudge: Nudge }>(`/api/actionables/${actionableId}/nudges`, data)
    return res.nudge
  }

  // --- AI assist (Milestone F) ---
  async decompose(goalsText: string): Promise<DecomposerResponse> {
    const storeId = authService.getCurrentStoreId()
    return apiClient.post<DecomposerResponse>('/api/goals/assist/decompose', {
      store_id: storeId, goals_text: goalsText,
    })
  }

  async draftNudge(indicatorState: any): Promise<{ nudge: { text: string; confidence: number } }> {
    const storeId = authService.getCurrentStoreId()
    return apiClient.post('/api/goals/assist/nudge', { store_id: storeId, indicator_state: indicatorState })
  }

  // --- Plan drafts (put_plan_draft gate) ---
  async listPlanDrafts(status?: 'pending' | 'applied' | 'dismissed', full = false): Promise<PlanDraft[]> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.get<{ drafts: PlanDraft[] }>('/api/goals/plan-draft', {
      store_id: storeId, ...(status ? { status } : {}), ...(full ? { full: 'true' } : {}),
    })
    return res.drafts || []
  }

  /** Resolve a draft exactly once: applied|dismissed. Creation of goals happens
   * separately through the normal gated endpoints — this only stamps the resolution. */
  async resolvePlanDraft(draftId: string, status: 'applied' | 'dismissed', note?: string): Promise<PlanDraft> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.put<{ draft: PlanDraft }>(`/api/goals/plan-draft/${draftId}`, {
      store_id: storeId, status, ...(note ? { note } : {}),
    })
    return res.draft
  }

  // --- Commercial-event calendar (C2): planning objects; readings crossing an event
  // window carry a MARKER as context, never a suppression. ---
  async listEvents(): Promise<CommercialEvent[]> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.get<{ events: CommercialEvent[] }>('/api/goals/events', { store_id: storeId })
    return res.events || []
  }

  async createEvent(data: Omit<CommercialEvent, 'event_id'>): Promise<CommercialEvent> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.post<{ event: CommercialEvent }>('/api/goals/events', { ...data, store_id: storeId })
    return res.event
  }

  async updateEvent(eventId: string, data: Partial<Omit<CommercialEvent, 'event_id'>>): Promise<CommercialEvent> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.put<{ event: CommercialEvent }>(`/api/goals/events/${eventId}`, { ...data, store_id: storeId })
    return res.event
  }

  async deleteEvent(eventId: string): Promise<void> {
    const storeId = authService.getCurrentStoreId()
    await apiClient.delete(`/api/goals/events/${eventId}?store_id=${storeId}`)
  }

  // --- Timeline (C7 v1) ---
  async getTimeline(target: { goalId?: string; actionableId?: string }, weeks = 8): Promise<Timeline> {
    const storeId = authService.getCurrentStoreId()
    const res = await apiClient.get<{ timeline: Timeline }>('/api/goals/timeline', {
      store_id: storeId, weeks,
      ...(target.goalId ? { goal_id: target.goalId } : {}),
      ...(target.actionableId ? { actionable_id: target.actionableId } : {}),
    })
    return res.timeline
  }
}

// Timeline (C7 v1): annotated series — the descriptive substitute for gated verdicts.
// Correlational register only: annotation adjacency never narrates cause.
export interface TimelineBucket {
  desde: string
  hasta: string
  dias: number
  dias_con_datos?: number
  valor: number | null
  c?: number
  n?: number
  banda?: { lo: number; hi: number } // 90% Jeffreys credible band, descriptive
}
export interface TimelineAnnotation {
  tipo: 'activacion' | 'evento' | 'frontera' | 'ancla_baseline' | 'nota'
  fecha: string
  hasta?: string
  label: string
}
export interface Timeline {
  schema: number
  store_id: string
  indicador: string
  metric_class: 'rate' | 'count' | 'continuous' | null
  escala: 'semana'
  rango: { desde: string; hasta: string } | null
  serie: TimelineBucket[]
  anotaciones: TimelineAnnotation[]
  nota: string
}

// Commercial event (C2): the merchant's revenue plan as data — name, range, associated
// actionables. Readings overlapping [desde, hasta] carry `eventos_en_ventana` markers.
export interface CommercialEvent {
  event_id: string
  nombre: string
  desde: string // YYYY-MM-DD inclusive
  hasta: string
  nota_comercial?: string
  acciones?: string[]
}

// AI-proposed plan (mirrors the Decomposer tool schema). Bindings may reference
// existing primitives (ref_status:'existing') or new ones to create ('pending_new').
export interface ProposedNewPrimitive {
  key: string
  kind: 'cohort' | 'custom_event'
  spec: any
  rationale?: string
}
export interface ProposedActionable {
  name: string
  intent?: string
  type: Actionable['type']
  indicators?: Indicator[]
  new_primitives?: ProposedNewPrimitive[]
}
export interface ProposedGoal {
  name: string
  description?: string
  primary_metric: Indicator
  target?: { value: number; type: 'relative_pct' | 'absolute' }
  impact_tier?: 'high' | 'medium' | 'low'
  actionables: ProposedActionable[]
}
export interface DecomposerResponse {
  plan: { goals: ProposedGoal[] }
  usage?: any
  catalog_summary?: { funnel_steps: number; cohorts: number; custom_events: number }
}

// Plan drafts (put_plan_draft, TNX-MCP-SPEC §7.5): a meeting's proposed plan persisted
// as a draft the consultant applies/dismisses here — the MCP write never creates goals.
export interface PlanDraftGoal {
  name: string
  /** Delta semantics: reference an EXISTING meta (from get_plan) — apply attaches the
   *  actionables to it instead of creating a goal. Name-match (exact) is the fallback. */
  goal_id?: string
  description?: string
  primary_metric?: Indicator // may arrive unbound; apply skips NEW goals without a bound metric
  target?: { value: number; type: 'relative_pct' | 'absolute' }
  impact_tier?: 'high' | 'medium' | 'low'
  evidence?: Partial<Evidence>
  actionables?: (ProposedActionable & { evidence?: Partial<Evidence> })[]
}
export interface PlanDraftPregunta {
  question: string
  notes?: string
  risk_level?: 'safe' | 'balanced' | 'agile'
  indicator?: Indicator
}
export interface PlanDraft {
  draft_id: string
  status: 'pending' | 'applied' | 'dismissed'
  plan_doc?: { goals: PlanDraftGoal[]; preguntas?: PlanDraftPregunta[] }
  meeting_context?: string | null
  proposed_by?: string | null
  created_at: string
  // summary projection (list without full=true)
  goals?: number
  actionables?: number
  preguntas?: number
  resolution_note?: string
}

export const goalService = new GoalService()
